import { BusinessHoursDay, ProviderPublic, ProviderStaticConfig } from '~/lib/model';
import { TwtProviderId, TwtProviderStaticData } from '~/tenants/twt/model/TwtProvider';
import { DateTime } from 'luxon';

export enum TwtVersion {
  V1 = '2024-01-01T00:00:00.000Z',
  V2 = '2024-10-21T18:40:42.000Z',
  V3 = '2025-01-29T18:48:42.802',
  V4 = '2025-03-01T15:30:26.530Z',
  V5 = '2025-03-21T00:13:12.197Z',
  V6 = '2025-04-01T18:25:30.807Z',
  V7 = '2025-04-02T18:21:41.378Z',
  V8 = '2025-04-05T21:17:54.605Z',
  V9 = '2025-04-10T16:40:45.128Z',
  V10 = '2025-04-14T16:43:37.607Z',
  V11 = '2025-04-22T00:39:32.891Z',
  V12 = '2025-05-05T13:37:02.529Z',
  V13 = '2025-05-15T13:21:32.415Z',
  V14 = '2025-05-26T21:51:02.983Z',
  V15 = '2025-08-04T00:27:15.517Z',
  V16 = '2025-09-14T21:37:34.631Z',
  V17 = '2025-10-07T13:48:26.749Z',
  V18 = '2025-10-23T22:13:24.975Z',
  V19 = '2026-01-12T15:30:59.973Z',
  V20 = '2026-02-26T15:33:57.085Z',
  V21 = '2026-03-29T22:50:35.489Z',
  V22 = '2026-04-26T12:41:33.950Z',
  V23 = '2026-05-26T00:46:27.263Z',
  V24 = '2026-06-01T00:00:00.000Z',
  V25 = '2026-07-08T18:17:15.040Z',
  V26 = '2026-08-14T02:37:14.000Z',
  V27 = '2026-09-08T21:11:36.116Z',
  // Mini aerial promotion ends. Set to just after the deploy that ships it
  // (the 14:43 UTC original never deployed), so no order placed at $110
  // is repriced.
  V28 = '2026-09-25T19:00:00.000Z',
  // The commercial 3D scan's $350 minimum (TWT_COMMERCIAL_3D_MINIMUM), and its
  // photographer pay the residential scan's (Paul, 10/19/26). PLACEHOLDER:
  // set to deploy time + 15 min when shipping, so no order already placed is
  // repriced.
  V29 = '2026-10-20T03:00:00.000Z',
}

/** The least a commercial 3D scan costs the customer from TwtVersion.V29 (0.15 x sqft above it). */
export const TWT_COMMERCIAL_3D_MINIMUM = '350';

export function twt_version(date: TDateISO | DateTime, version: TwtVersion) {
  return (typeof date === 'string' ? new Date(date) : date.toJSDate()) >= new Date(version);
}

/**
 * The mini aerial promotion: orders CREATED from V26 up to (not including)
 * V28 price the mini aerial at $110. Named here, rather than left inline in
 * TwtAerialJob's revenue lines, because Ask Twist Support explains it too
 * ("why is the mini aerial $110 on this order?") — the assistant and the
 * invoice must read the same dates, or the first cutoff change makes the
 * assistant wrong while the invoice stays right.
 */
export const TWT_MINI_AERIAL_PROMO = { from: TwtVersion.V26, until: TwtVersion.V28, price: 110 } as const;

/** The residential mini aerial's price for an order created at `created` (before any V4 remapping). */
export function twt_mini_aerial_price(created: TDateISO | DateTime): number {
  if (twt_version(created, TWT_MINI_AERIAL_PROMO.from) && !twt_version(created, TWT_MINI_AERIAL_PROMO.until)) {
    return TWT_MINI_AERIAL_PROMO.price;
  }

  return twt_version(created, TwtVersion.V21) ? 145 : 135;
}

/**
 * The PUBLIC half of each provider's record: what may ship to the browser.
 * Email, phone, addresses and pay live in ./providers.server.ts and are
 * merged into these same objects on the server (tenants/providers.server.ts).
 */
const TWT_PROVIDER_DATA_PUBLIC: Record<TwtProviderId, ProviderPublic<TwtProviderStaticData & ProviderStaticConfig>> = {
  [TwtProviderId.ALEC_KNIGHT]: {
    senior: true,
    schedule: true,
    first: 'Alec',
    last: 'Knight',
    headshot: 'tenant/twt/provider/alec.jpg',
    location: 'atx',

    travel_start: '09:45',
    travel_end: '16:30',

    rush_end_one: '15:30',
    rush_weekend: true,

    photo_onsite_0_1000: 45,
    photo_onsite_1001_2000: 45,
    photo_onsite_2001_3000: 60,
    photo_onsite_3001_4000: 75,
    photo_onsite_4001_5000: 90,
    photo_onsite_5001_6000: 105,
    photo_onsite_every_1000_after_6000: 15,

    scan_onsite_0_1000: 45,
    scan_onsite_1001_2000: 45,
    scan_onsite_2001_3000: 45,
    scan_onsite_3001_4000: 60,
    scan_onsite_4001_5000: 90,
    scan_onsite_5001_6000: 105,
    scan_onsite_every_1000_after_6000: 15,

    highlights_onsite: 30,

    aerial_onsite_mini: 30,
    aerial_onsite_full: 30,
    aerial_onsite_video: 30,
    aerial_onsite_photo_video: 45,

    video_onsite_0_2000: 60,
    video_onsite_2001_3000: 75,
    video_onsite_over_3000: 90,

    reel_onsite: 60,

    floorplan_onsite_0_1000: 15,
    floorplan_onsite_1001_2000: 15,
    floorplan_onsite_2001_3000: 20,
    floorplan_onsite_3001_4000: 20,
    floorplan_onsite_4001_5000: 30,
    floorplan_onsite_5001_6000: 30,
    floorplan_onsite_every_1000_after_6000: 5,
  },
  [TwtProviderId.ALEX_CASTRO]: {
    schedule: true,
    first: 'Alex',
    last: 'Castro',
    headshot: 'tenant/twt/provider/alex.jpg',
    location: 'atx',

    travel_start: '08:00',
    travel_end: '17:00',
    travel_end_ideal: '16:30',

    photo_onsite_0_1000: 60,
    photo_onsite_1001_2000: 60,
    photo_onsite_2001_3000: 90,
    photo_onsite_3001_4000: 105,
    photo_onsite_4001_5000: 105,
    photo_onsite_5001_6000: 120,
    photo_onsite_every_1000_after_6000: 15,

    highlights_onsite: 40,

    rush_end_one: '16:30',
    rush_weekend: 'warning',

    twilight_days: [BusinessHoursDay.Monday, BusinessHoursDay.Tuesday, BusinessHoursDay.Wednesday, BusinessHoursDay.Thursday, BusinessHoursDay.Friday],

    scan_mppro2: true,
    scan_ricoh: true,
    scan_nucleus: true,
    scan_onsite_0_1000: 30,
    scan_onsite_1001_2000: 45,
    scan_onsite_2001_3000: 60,
    scan_onsite_3001_4000: 60,
    scan_onsite_4001_5000: 75,
    scan_onsite_5001_6000: 75,

    floorplan_onsite_0_1000: 10,
    floorplan_onsite_1001_2000: 10,
    floorplan_onsite_2001_3000: 15,
    floorplan_onsite_3001_4000: 20,
    floorplan_onsite_4001_5000: 20,
    floorplan_onsite_5001_6000: 20,
    floorplan_onsite_every_1000_after_6000: 5,

    aerial_onsite_mini: 30,
    aerial_onsite_full: 45,
  },
  [TwtProviderId.ALLISON_CARTWRIGHT]: {
    senior: true,
    schedule: true,
    first: 'Allison',
    hidden: true,
    last: 'Cartwright',
    headshot: 'tenant/twt/provider/allison.jpg',
    location: 'atx',

    travel_start: '09:00',

    photo_onsite_0_1000: 30,
    photo_onsite_1001_2000: 45,
    photo_onsite_2001_3000: 45,
    photo_onsite_3001_4000: 60,
    photo_onsite_4001_5000: 90,
    photo_onsite_5001_6000: 90,
    photo_onsite_every_1000_after_6000: 15,

    scan_ricoh: true,
    scan_onsite_0_1000: 30,
    scan_onsite_1001_2000: 30,
    scan_onsite_2001_3000: 45,
    scan_onsite_3001_4000: 60,
    scan_onsite_4001_5000: 90,
    scan_onsite_5001_6000: 90,
    scan_onsite_every_1000_after_6000: 15,

    highlights_onsite: 30,
  },
  [TwtProviderId.AMANDA_TERRY]: {
    commercial: true,
    senior: true,
    schedule: true,
    hidden: true,
    first: 'Amanda',
    last: 'Terry',
    headshot: 'tenant/twt/provider/amanda.jpg',
    location: 'san',

    travel_start: '08:00',
    travel_end: '16:00',

    photo_onsite_0_1000: 30,
    photo_onsite_1001_2000: 30,
    photo_onsite_2001_3000: 30,
    photo_onsite_3001_4000: 45,
    photo_onsite_4001_5000: 60,
    photo_onsite_5001_6000: 60,
    photo_onsite_every_1000_after_6000: 15,

    scan_mppro2: true,
    scan_ricoh: true,
    scan_onsite_0_1000: 30,
    scan_onsite_1001_2000: 30,
    scan_onsite_2001_3000: 30,
    scan_onsite_3001_4000: 30,
    scan_onsite_4001_5000: 45,
    scan_onsite_5001_6000: 60,
    scan_onsite_every_1000_after_6000: 15,

    twilight_weekly_max: 1,

    highlights_onsite: 30,

    floorplan_onsite_0_1000: 15,
    floorplan_onsite_1001_2000: 15,
    floorplan_onsite_2001_3000: 15,
    floorplan_onsite_3001_4000: 15,
    floorplan_onsite_4001_5000: 20,
    floorplan_onsite_5001_6000: 30,
    floorplan_onsite_every_1000_after_6000: 15,
  },
  [TwtProviderId.ANDREW_LANKES]: {
    schedule: true,
    first: 'Andrew',
    last: 'Lankes',
    headshot: 'tenant/twt/provider/andrew.jpg',
    location: 'atx',

    travel_start: '09:00',
    travel_end: '16:00',
    rush_end_one: '14:00',

    photo_onsite_0_1000: 30,
    photo_onsite_1001_2000: 30,
    photo_onsite_2001_3000: 30,
    photo_onsite_3001_4000: 45,
    photo_onsite_4001_5000: 45,
    photo_onsite_5001_6000: 60,
    photo_onsite_every_1000_after_6000: 15,

    twilight_weekly_max: 1,
    twilight_days: [
      BusinessHoursDay.Monday,
      BusinessHoursDay.Tuesday,
      BusinessHoursDay.Wednesday,
      BusinessHoursDay.Thursday,
    ],
    twilight_postals_includes: [
      '78751',
      '78752',
      '78753',
      '78754',
      '78755',
      '78756',
      '78757',
      '78758',
      '78759',
      '78721',
      '78722',
      '78723',
      '78727',
      '78731',
      '78660',
    ],

    scan_ricoh: true,
    scan_onsite_0_1000: 30,
    scan_onsite_1001_2000: 30,
    scan_onsite_2001_3000: 30,
    scan_onsite_3001_4000: 60,
    scan_onsite_4001_5000: 75,
    scan_onsite_5001_6000: 90,
    scan_onsite_every_1000_after_6000: 15,

    floorplan_onsite_0_1000: 15,
    floorplan_onsite_1001_2000: 15,
    floorplan_onsite_2001_3000: 15,
    floorplan_onsite_3001_4000: 15,
    floorplan_onsite_4001_5000: 25,
    floorplan_onsite_5001_6000: 35,
    floorplan_onsite_every_1000_after_6000: 5,

    highlights_onsite: 30,
    aerial_onsite_mini: 30,
    aerial_onsite_full: 30,
    aerial_onsite_video: 30,
    aerial_onsite_photo_video: 30,
  },
  [TwtProviderId.ASHLEY_MATHEWS]: {
    schedule: true,
    first: 'Ash',
    last: 'Red',
    headshot: 'tenant/twt/provider/ashred.jpg',
    location: 'atx',

    travel_start: '08:30',
    travel_end: '13:30',

    video_onsite_0_2000: 60,
    video_onsite_2001_3000: 75,
    video_onsite_over_3000: 90,

    cameo_onsite: 30,

    reel_onsite: 60,

    photo_onsite_0_1000: 45,
    photo_onsite_1001_2000: 45,
    photo_onsite_2001_3000: 75,
    photo_onsite_3001_4000: 90,
    photo_onsite_4001_5000: 90,
    photo_onsite_5001_6000: 120,
    photo_onsite_every_1000_after_6000: 15,

    rush_avoid: true,
    rush_disable: true,

    twilight_days: [BusinessHoursDay.Monday],
    twilight_weekly_max: 1,

    scan_ricoh: true,
    scan_onsite_0_1000: 45,
    scan_onsite_1001_2000: 60,
    scan_onsite_2001_3000: 75,
    scan_onsite_3001_4000: 90,
    scan_onsite_4001_5000: 90,
    scan_onsite_5001_6000: 135,
    scan_onsite_every_1000_after_6000: 15,

    highlights_onsite: 30,

    aerial_onsite_mini: 30,
    aerial_onsite_full: 30,
    aerial_onsite_video: 30,
    aerial_onsite_photo_video: 45,
  },
  [TwtProviderId.BRYAN_MURPHY]: {
    commercial: true,
    schedule: true,
    first: 'Bryan',
    last: 'Murphy',
    headshot: 'tenant/twt/provider/bryan.jpg',
    location: 'atx',

    travel_start: '08:00',
    travel_end: '17:30',

    photo_onsite_0_1000: 30,
    photo_onsite_1001_2000: 30,
    photo_onsite_2001_3000: 45,
    photo_onsite_3001_4000: 45,
    photo_onsite_4001_5000: 60,
    photo_onsite_5001_6000: 60,
    photo_onsite_every_1000_after_6000: 15,

    rush_end_one: '17:00',
    rush_avoid: true,

    twilight_weekly_max: 4,
    twilight_weekly_ideal: 2,
    twilight_days_confirm: [BusinessHoursDay.Friday],

    scan_ricoh: true,
    scan_onsite_0_1000: 30,
    scan_onsite_1001_2000: 30,
    scan_onsite_2001_3000: 45,
    scan_onsite_3001_4000: 45,
    scan_onsite_4001_5000: 45,
    scan_onsite_5001_6000: 90,
    scan_onsite_every_1000_after_6000: 15,

    floorplan_onsite_0_1000: 10,
    floorplan_onsite_1001_2000: 10,
    floorplan_onsite_2001_3000: 10,
    floorplan_onsite_3001_4000: 10,
    floorplan_onsite_4001_5000: 10,
    floorplan_onsite_5001_6000: 10,
    floorplan_onsite_every_1000_after_6000: 5,

    highlights_onsite: 30,
    reel_onsite: 60,
    cameo_onsite: 30,

    aerial_onsite_mini: 15,
    aerial_onsite_full: 30,
    aerial_onsite_video: 45,
    aerial_onsite_photo_video: 45,

    video_onsite_0_2000: 75,
    video_onsite_2001_3000: 75,
    video_onsite_over_3000: 75,
  },
  [TwtProviderId.CALEB_RUCKEL]: {
    commercial: true,
    schedule: true,
    first: 'Caleb',
    last: 'Ruckel',
    headshot: 'tenant/twt/provider/caleb.jpg',
    location: 'atx',

    travel_start: '08:30',
    travel_end: '18:00',

    photo_onsite_0_1000: 45,
    photo_onsite_1001_2000: 60,
    photo_onsite_2001_3000: 75,
    photo_onsite_3001_4000: 90,
    photo_onsite_4001_5000: 105,
    photo_onsite_5001_6000: 120,
    photo_onsite_every_1000_after_6000: 15,

    rush_end_one: '16:00',

    twilight_daily_shoots: 2,
    twilight_weekly_max: 3,
    twilight_weekly_ideal: 2,
    twilight_delay: '10:30',

    scan_ricoh: true,
    scan_onsite_0_1000: 45,
    scan_onsite_1001_2000: 45,
    scan_onsite_2001_3000: 60,
    scan_onsite_3001_4000: 90,
    scan_onsite_4001_5000: 120,
    scan_onsite_5001_6000: 150,
    scan_onsite_every_1000_after_6000: 15,

    floorplan_onsite_0_1000: 30,
    floorplan_onsite_1001_2000: 30,
    floorplan_onsite_2001_3000: 30,
    floorplan_onsite_3001_4000: 30,
    floorplan_onsite_4001_5000: 30,
    floorplan_onsite_5001_6000: 30,
    floorplan_onsite_every_1000_after_6000: 0,
    floorplan_onsite_with_photos: 5,

    highlights_onsite: 30,
    aerial_onsite_mini: 30,
    aerial_onsite_full: 45,
  },
  [TwtProviderId.DANA_RASMUSSEN]: {
    schedule: true,
    first: 'Dana',
    last: 'Rasmussen',
    headshot: 'tenant/twt/provider/dana.jpg',
    location: 'atx',

    travel_start: '08:30',
    travel_end: '17:00',

    twilight_days: [
      BusinessHoursDay.Monday,
      BusinessHoursDay.Tuesday,
      BusinessHoursDay.Wednesday,
      BusinessHoursDay.Thursday,
      BusinessHoursDay.Friday,
    ],

    photo_onsite_0_1000: 45,
    photo_onsite_1001_2000: 60,
    photo_onsite_2001_3000: 60,
    photo_onsite_3001_4000: 75,
    photo_onsite_4001_5000: 90,
    photo_onsite_5001_6000: 105,
    photo_onsite_every_1000_after_6000: 15,
    scan_ricoh: true,
    scan_onsite_0_1000: 45,
    scan_onsite_1001_2000: 45,
    scan_onsite_2001_3000: 75,
    scan_onsite_3001_4000: 75,
    scan_onsite_4001_5000: 90,
    scan_onsite_5001_6000: 120,
    scan_onsite_every_1000_after_6000: 15,

    highlights_onsite: 45,

    aerial_onsite_mini: 30,
    aerial_onsite_full: 30,
    aerial_onsite_video: 60,
    aerial_onsite_photo_video: 90,
    // aerial_end_video: '15:00',
    // aerial_end_photo: '15:30',
  },
  [TwtProviderId.DEAN_JANKA]: {
    schedule: false,
    hidden: true,
    first: 'Dean',
    last: 'Janka',
    headshot: 'tenant/twt/provider/dean2.jpg',
    location: 'atx',

    travel_start: '09:00',
    travel_end: '17:00',

    rush_avoid: true,

    days: {
      [BusinessHoursDay.Monday]: {},
      [BusinessHoursDay.Tuesday]: {},
      [BusinessHoursDay.Wednesday]: {},
      [BusinessHoursDay.Thursday]: {},
      [BusinessHoursDay.Friday]: { confirm: true },
    },

    photo_onsite_0_1000: 45,
    photo_onsite_1001_2000: 60,
    photo_onsite_2001_3000: 75,
    photo_onsite_3001_4000: 90,
    photo_onsite_4001_5000: 120,
    photo_onsite_5001_6000: 135,
    photo_onsite_every_1000_after_6000: 15,

    scan_nucleus: true,
    scan_onsite_0_1000: 30,
    scan_onsite_1001_2000: 30,
    scan_onsite_2001_3000: 45,
    scan_onsite_3001_4000: 60,
    scan_onsite_4001_5000: 90,
    scan_onsite_5001_6000: 105,
    scan_onsite_every_1000_after_6000: 15,

    floorplan_onsite_0_1000: 5,
    floorplan_onsite_1001_2000: 5,
    floorplan_onsite_2001_3000: 10,
    floorplan_onsite_3001_4000: 10,
    floorplan_onsite_4001_5000: 15,
    floorplan_onsite_5001_6000: 15,
    floorplan_onsite_every_1000_after_6000: 5,

    aerial_onsite_mini: 15,
    aerial_onsite_full: 30,
    aerial_onsite_video: 30,
    aerial_onsite_photo_video: 45,

    highlights_onsite: 15,
  },
  [TwtProviderId.JEFF_LOFTIN]: {
    schedule: true,
    first: 'Jeff',
    last: 'Loftin',
    headshot: 'tenant/twt/provider/jeff.jpg',
    location: 'atx',

    travel_start: '09:00',
    travel_end: '17:00',
    rush_end_one: '14:00',

    photo_onsite_0_1000: 45,
    photo_onsite_1001_2000: 45,
    photo_onsite_2001_3000: 60,
    photo_onsite_3001_4000: 75,
    photo_onsite_4001_5000: 90,
    photo_onsite_5001_6000: 105,
    photo_onsite_every_1000_after_6000: 15,

    scan_ricoh: true,
    scan_onsite_0_1000: 30,
    scan_onsite_1001_2000: 45,
    scan_onsite_2001_3000: 60,
    scan_onsite_3001_4000: 75,
    scan_onsite_4001_5000: 90,
    scan_onsite_5001_6000: 105,
    scan_onsite_every_1000_after_6000: 15,

    floorplan_onsite_0_1000: 10,
    floorplan_onsite_1001_2000: 10,
    floorplan_onsite_2001_3000: 10,
    floorplan_onsite_3001_4000: 15,
    floorplan_onsite_4001_5000: 15,
    floorplan_onsite_5001_6000: 15,
    floorplan_onsite_every_1000_after_6000: 5,

    highlights_onsite: 45,
  },
  [TwtProviderId.KYLE_GIRGUS]: {
    schedule: true,
    first: 'Kyle',
    headshot: 'tenant/twt/provider/kyle.jpeg',
    last: 'Girgus',
    location: 'atx',

    travel_start: '08:30',
    travel_end: '16:00',
    rush_end_one: '12:00',

    days: {
      [BusinessHoursDay.Monday]: { photo_daily: 4 },
      [BusinessHoursDay.Tuesday]: { photo_daily: 4 },
      [BusinessHoursDay.Wednesday]: { photo_daily: 4 },
      [BusinessHoursDay.Thursday]: { photo_daily: 2, travel_end: '14:30' },
      [BusinessHoursDay.Friday]: { photo_daily: 2, travel_end: '14:30' },
    },

    photo_onsite_0_1000: 15,
    photo_onsite_1001_2000: 30,
    photo_onsite_2001_3000: 45,
    photo_onsite_3001_4000: 45,
    photo_onsite_4001_5000: 45,
    photo_onsite_5001_6000: 60,
    photo_onsite_every_1000_after_6000: 15,

    twilight_days: [BusinessHoursDay.Monday, BusinessHoursDay.Tuesday, BusinessHoursDay.Wednesday],
    twilight_weekly_max: 2,

    scan_ricoh: true,
    scan_onsite_0_1000: 30,
    scan_onsite_1001_2000: 30,
    scan_onsite_2001_3000: 45,
    scan_onsite_3001_4000: 60,
    scan_onsite_4001_5000: 60,
    scan_onsite_5001_6000: 90,
    scan_onsite_every_1000_after_6000: 30,

    floorplan_onsite_0_1000: 15,
    floorplan_onsite_1001_2000: 15,
    floorplan_onsite_2001_3000: 30,
    floorplan_onsite_3001_4000: 30,
    floorplan_onsite_4001_5000: 45,
    floorplan_onsite_5001_6000: 45,
    floorplan_onsite_every_1000_after_6000: 45,

    highlights_onsite: 30,

    aerial_onsite_mini: 15,
    aerial_onsite_full: 30,
    aerial_onsite_video: 30,
    aerial_onsite_photo_video: 60,

    video_onsite_0_2000: 60,
    video_onsite_2001_3000: 60,
    video_onsite_over_3000: 60,

    // reel_onsite: 75,
  },
  [TwtProviderId.LAURA_DUNCAN]: {
    schedule: true,
    first: 'Laura',
    last: 'Duncan',
    headshot: 'tenant/twt/provider/laura.jpg',
    location: 'atx',

    travel_start: '08:30',
    travel_end: '16:30',
    rush_end_one: '15:00',
    rush_end_one_twilight: '14:00',
    rush_end_two: '14:00',

    photo_onsite_0_1000: 30,
    photo_onsite_1001_2000: 45,
    photo_onsite_2001_3000: 45,
    photo_onsite_3001_4000: 75,
    photo_onsite_4001_5000: 105,
    photo_onsite_5001_6000: 120,
    photo_onsite_every_1000_after_6000: 15,

    twilight_days: [BusinessHoursDay.Monday, BusinessHoursDay.Wednesday],
    twilight_days_confirm: [BusinessHoursDay.Tuesday, BusinessHoursDay.Thursday, BusinessHoursDay.Friday],
    twilight_distance: 30,
    twilight_end: '14:30',

    scan_ricoh: true,
    scan_nucleus: true,
    scan_onsite_0_1000: 30,
    scan_onsite_1001_2000: 45,
    scan_onsite_2001_3000: 60,
    scan_onsite_3001_4000: 90,
    scan_onsite_4001_5000: 120,
    scan_onsite_5001_6000: 135,
    scan_onsite_every_1000_after_6000: 15,

    floorplan_onsite_0_1000: 10,
    floorplan_onsite_1001_2000: 10,
    floorplan_onsite_2001_3000: 10,
    floorplan_onsite_3001_4000: 15,
    floorplan_onsite_4001_5000: 15,
    floorplan_onsite_5001_6000: 15,
    floorplan_onsite_every_1000_after_6000: 5,

    highlights_onsite: 30,

    aerial_onsite_mini: 40,
    aerial_onsite_full: 40,
  },
  [TwtProviderId.LINAS_MILAVICKAS]: {
    commercial: true,
    schedule: true,
    first: 'Linas',
    last: 'Milavickas',
    senior: '2025-03-16T17:23:34.533Z',
    headshot: 'tenant/twt/provider/linas.jpg',
    location: 'atx',

    days: {
      [BusinessHoursDay.Friday]: {
        travel_end: '15:15',
      },
    },

    travel_start: '08:30',
    travel_end: '15:30',
    rush_end_one: '15:00',

    photo_onsite_0_1000: 30,
    photo_onsite_1001_2000: 45,
    photo_onsite_2001_3000: 45,
    photo_onsite_3001_4000: 45,
    photo_onsite_4001_5000: 60,
    photo_onsite_5001_6000: 75,
    photo_onsite_every_1000_after_6000: 15,

    twilight_daily_shoots: 4,
    twilight_days: [BusinessHoursDay.Monday, BusinessHoursDay.Tuesday, BusinessHoursDay.Wednesday],
    twilight_distance: 30,

    scan_ricoh: true,
    scan_onsite_0_1000: 15,
    scan_onsite_1001_2000: 15,
    scan_onsite_2001_3000: 30,
    scan_onsite_3001_4000: 30,
    scan_onsite_4001_5000: 45,
    scan_onsite_5001_6000: 60,
    scan_onsite_every_1000_after_6000: 15,

    floorplan_onsite_0_1000: 15,
    floorplan_onsite_1001_2000: 15,
    floorplan_onsite_2001_3000: 15,
    floorplan_onsite_3001_4000: 15,
    floorplan_onsite_4001_5000: 15,
    floorplan_onsite_5001_6000: 15,
    floorplan_onsite_every_1000_after_6000: 15,

    highlights_onsite: 30,

    aerial_onsite_mini: 15,
    aerial_onsite_full: 30,
    aerial_onsite_video: 30,
  },
  [TwtProviderId.LINN_RIVERS]: {
    hidden: true,
    schedule: true,
    first: 'Linn',
    last: 'Rivers',
    headshot: 'tenant/twt/provider/linn.jpg',
    location: 'atx',

    schedule_start: '09:00',

    photo_onsite_0_1000: 30,
    photo_onsite_1001_2000: 30,
    photo_onsite_2001_3000: 30,
    photo_onsite_3001_4000: 30,
    photo_onsite_4001_5000: 45,
    photo_onsite_5001_6000: 60,
    photo_onsite_every_1000_after_6000: 30,

    rush_end_one: '14:30',

    scan_ricoh: true,
    scan_mppro2: true,
    scan_onsite_0_1000: 30,
    scan_onsite_1001_2000: 30,
    scan_onsite_2001_3000: 45,
    scan_onsite_3001_4000: 60,
    scan_onsite_4001_5000: 90,
    scan_onsite_5001_6000: 120,
    scan_onsite_every_1000_after_6000: 30,

    aerial_onsite_mini: 15,
    aerial_onsite_full: 15,
    aerial_onsite_video: 30,
    aerial_onsite_photo_video: 60,
    aerial_confirm_plus_interior: true,
    aerial_confirm_plus_interior_photos: true,

    video_confirm: true,
    video_onsite_0_2000: 30,
    video_onsite_2001_3000: 30,
    video_onsite_over_3000: 30,
  },
  [TwtProviderId.MICHELLE_NOLAN]: {
    schedule: true,
    first: 'Michelle',
    last: 'Nolan',
    headshot: 'tenant/twt/provider/michelle.jpg',
    location: 'atx',

    days: {
      [BusinessHoursDay.Monday]: {},
      [BusinessHoursDay.Tuesday]: {},
      [BusinessHoursDay.Wednesday]: {},
      [BusinessHoursDay.Thursday]: {},
      [BusinessHoursDay.Friday]: {},
      [BusinessHoursDay.Saturday]: { schedule_end: '12:00', confirm: true },
    },

    travel_start: '08:00',
    travel_end: '16:00',
    rush_end_one: '15:00',
    fill: 4,

    photo_onsite_0_1000: 30,
    photo_onsite_1001_2000: 30,
    photo_onsite_2001_3000: 30,
    photo_onsite_3001_4000: 45,
    photo_onsite_4001_5000: 60,
    photo_onsite_5001_6000: 75,
    photo_onsite_every_1000_after_6000: 15,

    scan_mppro2: true,
    scan_nucleus: true,
    scan_ricoh: true,
    scan_onsite_0_1000: 15,
    scan_onsite_1001_2000: 30,
    scan_onsite_2001_3000: 45,
    scan_onsite_3001_4000: 45,
    scan_onsite_4001_5000: 60,
    scan_onsite_5001_6000: 75,
    scan_onsite_every_1000_after_6000: 15,

    floorplan_onsite_0_1000: 15,
    floorplan_onsite_1001_2000: 15,
    floorplan_onsite_2001_3000: 15,
    floorplan_onsite_3001_4000: 15,
    floorplan_onsite_4001_5000: 30,
    floorplan_onsite_5001_6000: 30,
    floorplan_onsite_every_1000_after_6000: 15,

    highlights_onsite: 30,
  },
  [TwtProviderId.PHILIP_ROGERS]: {
    schedule: false,
    first: 'Philip',
    last: 'Rogers',
    headshot: 'tenant/twt/provider/philip.jpg',
    location: 'atx',

    travel_start: '08:00',
    travel_start_ideal: '08:00',
    rush_end_one: '15:00',

    days: {
      [BusinessHoursDay.Monday]: {
        travel_end: '12:00',
      },
      [BusinessHoursDay.Tuesday]: { disable: true },
      [BusinessHoursDay.Wednesday]: { disable: true },
      [BusinessHoursDay.Thursday]: { disable: true },
      [BusinessHoursDay.Friday]: {},
    },

    photo_onsite_0_1000: 30,
    photo_onsite_1001_2000: 30,
    photo_onsite_2001_3000: 45,
    photo_onsite_3001_4000: 60,
    photo_onsite_4001_5000: 75,
    photo_onsite_5001_6000: 90,
    photo_onsite_every_1000_after_6000: 15,

    scan_mpcam: true,
    scan_ricoh: true,
    scan_onsite_0_1000: 30,
    scan_onsite_1001_2000: 45,
    scan_onsite_2001_3000: 60,
    scan_onsite_3001_4000: 75,
    scan_onsite_4001_5000: 90,
    scan_onsite_5001_6000: 105,
    scan_onsite_every_1000_after_6000: 15,

    floorplan_onsite_0_1000: 15,
    floorplan_onsite_1001_2000: 15,
    floorplan_onsite_2001_3000: 15,
    floorplan_onsite_3001_4000: 15,
    floorplan_onsite_4001_5000: 15,
    floorplan_onsite_5001_6000: 15,
    floorplan_onsite_every_1000_after_6000: 15,

    preference: false,

    highlights_onsite: 30,

    aerial_onsite_mini: 15,
    aerial_onsite_full: 30,
  },
  [TwtProviderId.RICKY_LANIER]: {
    schedule: true,
    first: 'Ricky',
    last: 'Lanier',
    headshot: 'tenant/twt/provider/ricky.jpg',
    location: 'atx',

    travel_start: '09:00',
    travel_end: '16:30',
    rush_end_one: '15:00',
    rush_avoid: true,

    days: {
      [BusinessHoursDay.Friday]: {
        travel_end: '15:30',
      },
    },

    photo_onsite_0_1000: 30,
    photo_onsite_1001_2000: 30,
    photo_onsite_2001_3000: 45,
    photo_onsite_3001_4000: 60,
    photo_onsite_4001_5000: 135,
    photo_onsite_5001_6000: 165,
    photo_onsite_every_1000_after_6000: 15,

    scan_mppro2: true,
    scan_ricoh: true,
    scan_nucleus: true,
    scan_onsite_0_1000: 30,
    scan_onsite_1001_2000: 45,
    scan_onsite_2001_3000: 60,
    scan_onsite_3001_4000: 75,
    scan_onsite_4001_5000: 120,
    scan_onsite_5001_6000: 135,
    scan_onsite_every_1000_after_6000: 15,

    floorplan_onsite_0_1000: 5,
    floorplan_onsite_1001_2000: 10,
    floorplan_onsite_2001_3000: 10,
    floorplan_onsite_3001_4000: 10,
    floorplan_onsite_4001_5000: 15,
    floorplan_onsite_5001_6000: 15,
    floorplan_onsite_every_1000_after_6000: 5,

    highlights_onsite: 30,
  },
  [TwtProviderId.TRAVIS_BAKER]: {
    commercial: true,
    senior: true,
    schedule: true,
    first: 'Travis',
    last: 'Baker',
    headshot: 'tenant/twt/provider/travis.jpg',
    location: 'atx',

    travel_start: '08:30',
    travel_end: '16:00',
    twilight_end: '15:00',
    rush_end_one: '15:00',

    photo_onsite_0_1000: 30,
    photo_onsite_1001_2000: 30,
    photo_onsite_2001_3000: 30,
    photo_onsite_3001_4000: 45,
    photo_onsite_4001_5000: 60,
    photo_onsite_5001_6000: 75,
    photo_onsite_every_1000_after_6000: 30,

    days: {
      [BusinessHoursDay.Tuesday]: {
        travel_end: '14:00',
        no_rush: true,
        no_twilight: true,
      },
    },

    twilight_daily_shoots: 5,
    twilight_weekly_max: 2,
    twilight_days: [
      BusinessHoursDay.Monday,
      BusinessHoursDay.Wednesday,
      BusinessHoursDay.Thursday,
      BusinessHoursDay.Friday,
    ],
    twilight_postals_excludes: ['78737'],
    twilight_distance: 45,

    scan_mppro2: true,
    scan_ricoh: true,
    scan_nucleus: true,
    scan_onsite_0_1000: 45,
    scan_onsite_1001_2000: 60,
    scan_onsite_2001_3000: 75,
    scan_onsite_3001_4000: 90,
    scan_onsite_4001_5000: 120,
    scan_onsite_5001_6000: 150,
    scan_onsite_every_1000_after_6000: 30,

    floorplan_onsite_0_1000: 30,
    floorplan_onsite_1001_2000: 30,
    floorplan_onsite_2001_3000: 30,
    floorplan_onsite_3001_4000: 30,
    floorplan_onsite_4001_5000: 30,
    floorplan_onsite_5001_6000: 30,

    highlights_onsite: 30,

    aerial_onsite_mini: 30,
    aerial_onsite_full: 45,
    aerial_onsite_video: 60,
    aerial_onsite_photo_video: 90,

    reel_onsite: 75,
    cameo_onsite: 30,

    video_onsite_0_2000: 90,
    video_onsite_2001_3000: 90,
    video_onsite_over_3000: 90,
  },
  [TwtProviderId.ENRIQUE_LOPEZ]: {
    commercial: true,
    senior: false,
    schedule: true,
    preference: false,
    first: 'Enrique',
    last: 'Lopez',
    headshot: 'avatar.jpg',
    location: 'san',

    travel_start: '09:00',
    travel_end: '17:00',
    travel_end_ideal: '16:00',

    rush_avoid: true,

    photo_onsite_0_1000: 30,
    photo_onsite_1001_2000: 30,
    photo_onsite_2001_3000: 30,
    photo_onsite_3001_4000: 45,
    photo_onsite_4001_5000: 60,
    photo_onsite_5001_6000: 75,
    photo_onsite_every_1000_after_6000: 15,

    twilight_days: [BusinessHoursDay.Tuesday, BusinessHoursDay.Wednesday],
    twilight_days_ideal: [BusinessHoursDay.Wednesday],
    twilight_weekly_max: 2,

    scan_mppro2: true,
    scan_ricoh: true,
    scan_nucleus: true,
    scan_onsite_0_1000: 30,
    scan_onsite_1001_2000: 30,
    scan_onsite_2001_3000: 45,
    scan_onsite_3001_4000: 60,
    scan_onsite_4001_5000: 75,
    scan_onsite_5001_6000: 90,
    scan_onsite_every_1000_after_6000: 15,

    highlights_onsite: 30,
    aerial_onsite_mini: 15,
    aerial_onsite_full: 15,
    aerial_onsite_video: 30,
    aerial_onsite_photo_video: 45,

    video_onsite_0_2000: 60,
    video_onsite_2001_3000: 60,
    video_onsite_over_3000: 60,

    reel_onsite: 45,
    cameo_onsite: 30,
  },
  [TwtProviderId.TRENT_HEADY]: {
    commercial: true,
    senior: true,
    schedule: true,
    first: 'Trent',
    last: 'Heady',
    headshot: 'tenant/twt/provider/trent.jpg',
    location: 'san',

    travel_start: '09:00',
    travel_end: '17:00',
    travel_end_ideal: '16:00',

    rush_avoid: true,

    photo_onsite_0_1000: 30,
    photo_onsite_1001_2000: 30,
    photo_onsite_2001_3000: 30,
    photo_onsite_3001_4000: 45,
    photo_onsite_4001_5000: 60,
    photo_onsite_5001_6000: 75,
    photo_onsite_every_1000_after_6000: 15,

    twilight_days: [BusinessHoursDay.Tuesday, BusinessHoursDay.Wednesday],
    twilight_days_ideal: [BusinessHoursDay.Wednesday],
    twilight_weekly_max: 2,

    scan_mppro2: true,
    scan_ricoh: true,
    scan_nucleus: true,
    scan_onsite_0_1000: 30,
    scan_onsite_1001_2000: 30,
    scan_onsite_2001_3000: 45,
    scan_onsite_3001_4000: 60,
    scan_onsite_4001_5000: 75,
    scan_onsite_5001_6000: 90,
    scan_onsite_every_1000_after_6000: 15,

    highlights_onsite: 30,
    aerial_onsite_mini: 15,
    aerial_onsite_full: 15,
    aerial_onsite_video: 30,
    aerial_onsite_photo_video: 45,

    video_onsite_0_2000: 60,
    video_onsite_2001_3000: 60,
    video_onsite_over_3000: 60,

    reel_onsite: 45,
    cameo_onsite: 30,
  },
  // [TwtProviderId.PARIS_HOLLEY]: {
  //   schedule: true,
  //   first: 'Paris',
  //   last: 'Holley',
  //   location: 'san',
  //   headshot: 'avatar.jpg',
  // },
  // [TwtProviderId.NICK_RUCKEL]: {
  //   first: 'Nick',
  //   last: 'Ruckel',
  // },
  [TwtProviderId.RYAN_LUCK]: {
    first: 'Ryan',
    last: 'Luck',
    headshot: 'tenant/twt/provider/ryan.jpg',
    location: 'atx',
    schedule: false,
    hidden: true,

    photo_onsite_0_1000: 30,
    photo_onsite_1001_2000: 45,
    photo_onsite_2001_3000: 60,
    photo_onsite_3001_4000: 90,
    photo_onsite_4001_5000: 90,
    photo_onsite_5001_6000: 120,
    photo_onsite_every_1000_after_6000: 15,

    scan_insta360: true,
    scan_onsite_0_1000: 30,
    scan_onsite_1001_2000: 45,
    scan_onsite_2001_3000: 60,
    scan_onsite_3001_4000: 90,
    scan_onsite_4001_5000: 120,
    scan_onsite_5001_6000: 135,

    //reel_onsite: 60,
    cameo_onsite: 30,

    aerial_onsite_mini: 15,
    aerial_onsite_full: 45,
    aerial_onsite_video: 60,
    aerial_onsite_photo_video: 75,

    video_onsite_0_2000: 60,
    video_onsite_2001_3000: 75,
    video_onsite_over_3000: 90,
  },
  [TwtProviderId.JEFF_BUSH]: {
    first: 'Jeff',
    last: 'Bush',
    headshot: 'avatar.jpg',
    location: 'atx',
    schedule: true,
    hidden: true,
    schedule_start: '09:00',
    schedule_end: '15:30',

    aerial_onsite_mini: 30,
    aerial_onsite_full: 45,
    aerial_onsite_video: 60,
    aerial_onsite_photo_video: 90,

    reel_onsite: 75,
    cameo_onsite: 30,

    video_onsite_0_2000: 90,
    video_onsite_2001_3000: 90,
    video_onsite_over_3000: 90,
  },
  [TwtProviderId.BENNY_MATE]: {
    schedule: false,
    hidden: true,
    first: 'Benny',
    last: 'Mate',
    preference: false,
    headshot: 'tenant/twt/provider/benny.jpg',
    location: 'san',

    days: {
      [BusinessHoursDay.Monday]: { disable: true },
      [BusinessHoursDay.Tuesday]: { disable: true },
      [BusinessHoursDay.Wednesday]: { disable: true },
      [BusinessHoursDay.Thursday]: { travel_start: '09:00', travel_end: '13:00' },
      [BusinessHoursDay.Friday]: { travel_start: '09:00', travel_end: '18:00' },
      [BusinessHoursDay.Saturday]: { travel_start: '10:00', travel_end: '15:00' },
      [BusinessHoursDay.Sunday]: { disable: true },
    },

    photo_onsite_0_1000: 30,
    photo_onsite_1001_2000: 30,
    photo_onsite_2001_3000: 45,
    photo_onsite_3001_4000: 60,
    photo_onsite_4001_5000: 75,
    photo_onsite_5001_6000: 90,
    photo_onsite_every_1000_after_6000: 15,

    scan_mpcam: true,
    scan_ricoh: true,
    scan_onsite_0_1000: 30,
    scan_onsite_1001_2000: 45,
    scan_onsite_2001_3000: 60,
    scan_onsite_3001_4000: 75,
    scan_onsite_4001_5000: 90,
    scan_onsite_5001_6000: 105,
    scan_onsite_every_1000_after_6000: 15,

    floorplan_onsite_0_1000: 15,
    floorplan_onsite_1001_2000: 15,
    floorplan_onsite_2001_3000: 15,
    floorplan_onsite_3001_4000: 15,
    floorplan_onsite_4001_5000: 15,
    floorplan_onsite_5001_6000: 15,
    floorplan_onsite_every_1000_after_6000: 15,

    video_onsite_0_2000: 60,
    video_onsite_2001_3000: 60,
    video_onsite_over_3000: 60,

    highlights_onsite: 30,
  },
  [TwtProviderId.JOEY_SOLIS]: {
    schedule: false,
    hidden: true,
    senior: true,
    first: 'Joey',
    last: 'Solis',
    headshot: 'tenant/twt/provider/benny.jpg',
    location: 'san',

    travel_start: '10:00',
    travel_end: '13:00',

    days: {
      [BusinessHoursDay.Saturday]: { disable: true },
      [BusinessHoursDay.Sunday]: { disable: true },
    },

    photo_onsite_0_1000: 30,
    photo_onsite_1001_2000: 30,
    photo_onsite_2001_3000: 45,
    photo_onsite_3001_4000: 60,
    photo_onsite_4001_5000: 75,
    photo_onsite_5001_6000: 90,
    photo_onsite_every_1000_after_6000: 15,

    scan_mpcam: true,
    scan_ricoh: true,
    scan_onsite_0_1000: 30,
    scan_onsite_1001_2000: 45,
    scan_onsite_2001_3000: 60,
    scan_onsite_3001_4000: 75,
    scan_onsite_4001_5000: 90,
    scan_onsite_5001_6000: 105,
    scan_onsite_every_1000_after_6000: 15,

    floorplan_onsite_0_1000: 15,
    floorplan_onsite_1001_2000: 15,
    floorplan_onsite_2001_3000: 15,
    floorplan_onsite_3001_4000: 15,
    floorplan_onsite_4001_5000: 15,
    floorplan_onsite_5001_6000: 15,
    floorplan_onsite_every_1000_after_6000: 15,

    highlights_onsite: 30,

    aerial_onsite_mini: 15,
    aerial_onsite_full: 15,
    aerial_onsite_video: 30,
    aerial_onsite_photo_video: 45,
  },
  [TwtProviderId.DEZAN_HERNANDEZ]: {
    commercial: true,
    senior: true,
    schedule: true,
    first: 'Dezan',
    last: 'Hernandez',
    headshot: 'avatar.jpg',
    location: 'hou',

    travel_start: '09:00',
    travel_end: '17:00',
    travel_end_ideal: '16:00',

    rush_avoid: true,

    photo_onsite_0_1000: 30,
    photo_onsite_1001_2000: 30,
    photo_onsite_2001_3000: 30,
    photo_onsite_3001_4000: 45,
    photo_onsite_4001_5000: 60,
    photo_onsite_5001_6000: 75,
    photo_onsite_every_1000_after_6000: 15,

    twilight_days: [BusinessHoursDay.Tuesday, BusinessHoursDay.Wednesday],
    twilight_days_ideal: [BusinessHoursDay.Wednesday],
    twilight_weekly_max: 2,

    scan_mppro2: true,
    scan_ricoh: true,
    scan_nucleus: true,
    scan_onsite_0_1000: 30,
    scan_onsite_1001_2000: 30,
    scan_onsite_2001_3000: 45,
    scan_onsite_3001_4000: 60,
    scan_onsite_4001_5000: 75,
    scan_onsite_5001_6000: 90,
    scan_onsite_every_1000_after_6000: 15,

    highlights_onsite: 30,
    aerial_onsite_mini: 15,
    aerial_onsite_full: 15,
    aerial_onsite_video: 30,
    aerial_onsite_photo_video: 45,

    video_onsite_0_2000: 60,
    video_onsite_2001_3000: 60,
    video_onsite_over_3000: 60,

    reel_onsite: 45,
    cameo_onsite: 30,
  },
};

/** Typed whole for the code that reads it; on the client the private fields are absent. */
export const TWT_PROVIDER_DATA = TWT_PROVIDER_DATA_PUBLIC as Record<TwtProviderId, TwtProviderStaticData & ProviderStaticConfig>;

export const TWT_TRAVEL_FEES_V21: Record<string, { revenue: number; expense: number }> = {
  '76540': { revenue: 90, expense: 72 },
  '76541': { revenue: 90, expense: 72 },
  '76542': { revenue: 90, expense: 72 },
  '76543': { revenue: 90, expense: 72 },
  '76547': { revenue: 90, expense: 72 },
  '76548': { revenue: 90, expense: 72 },
  '76549': { revenue: 90, expense: 72 },
  '76571': { revenue: 90, expense: 72 },
}

export const TWT_TRAVEL_FEES_V22: Record<string, { revenue: number; expense: number }> = {
  '76501': { revenue: 98, expense: 98 },
  '76502': { revenue: 98, expense: 98 },
  '76503': { revenue: 98, expense: 98 },
  '76504': { revenue: 98, expense: 98 },
  '76508': { revenue: 98, expense: 98 },
  '76522': { revenue: 98, expense: 98 },
  '76533': { revenue: 98, expense: 98 },
  '76540': { revenue: 98, expense: 98 },
  '76541': { revenue: 98, expense: 98 },
  '76542': { revenue: 98, expense: 98 },
  '76543': { revenue: 98, expense: 98 },
  '76544': { revenue: 98, expense: 98 },
  '76547': { revenue: 98, expense: 98 },
  '76548': { revenue: 98, expense: 98 },
  '76549': { revenue: 98, expense: 98 },
  '76559': { revenue: 98, expense: 98 },
};

export const TWT_TRAVEL_FEES_V24: Record<string, { revenue: number; expense: number }> = {
  '78947': { revenue: 98, expense: 98 },
};

export const TWT_TRAVEL_FEES: Record<string, { revenue: number; expense: number }> = {
  '76501': { revenue: 70, expense: 56 },
  '76502': { revenue: 70, expense: 56 },
  '76503': { revenue: 80, expense: 64 },
  '76504': { revenue: 90, expense: 72 },
  '76505': { revenue: 80, expense: 64 },
  '76508': { revenue: 70, expense: 56 },
  '76511': { revenue: 40, expense: 32 },
  '76513': { revenue: 90, expense: 72 },
  '76522': { revenue: 100, expense: 80 },
  '76523': { revenue: 70, expense: 56 },
  '76527': { revenue: 30, expense: 24 },
  '76530': { revenue: 40, expense: 32 },
  '76533': { revenue: 80, expense: 64 },
  '76534': { revenue: 60, expense: 48 },
  '76537': { revenue: 30, expense: 24 },
  '76539': { revenue: 100, expense: 80 },
  '76540': { revenue: 80, expense: 64 },
  '76541': { revenue: 80, expense: 64 },
  '76542': { revenue: 90, expense: 72 },
  '76543': { revenue: 110, expense: 88 },
  '76544': { revenue: 100, expense: 80 },
  '76546': { revenue: 80, expense: 64 },
  '76547': { revenue: 70, expense: 56 },
  '76548': { revenue: 70, expense: 56 },
  '76549': { revenue: 70, expense: 56 },
  '76550': { revenue: 120, expense: 96 },
  '76554': { revenue: 70, expense: 56 },
  '76556': { revenue: 90, expense: 72 },
  '76559': { revenue: 60, expense: 48 },
  '76564': { revenue: 90, expense: 72 },
  '76567': { revenue: 60, expense: 48 },
  '76569': { revenue: 100, expense: 80 },
  '76571': { revenue: 40, expense: 32 },
  '76573': { revenue: 30, expense: 24 },
  '76574': { revenue: 25, expense: 20 },
  '76577': { revenue: 50, expense: 40 },
  '76578': { revenue: 30, expense: 24 },
  '76579': { revenue: 100, expense: 80 },
  '78003': { revenue: 50, expense: 40 },
  '78004': { revenue: 40, expense: 32 },
  '78006': { revenue: 30, expense: 24 },
  '78008': { revenue: 40, expense: 32 },
  '78011': { revenue: 90, expense: 72 },
  '78012': { revenue: 90, expense: 72 },
  '78013': { revenue: 30, expense: 24 },
  '78015': { revenue: 20, expense: 16 },
  '78016': { revenue: 20, expense: 16 },
  '78023': { revenue: 15, expense: 12 },
  '76520': { revenue: 100, expense: 80 },
  '78026': { revenue: 30, expense: 24 },
  '78027': { revenue: 40, expense: 32 },
  '78028': { revenue: 85, expense: 68 },
  '78053': { revenue: 40, expense: 32 },
  '78056': { revenue: 30, expense: 24 },
  '78057': { revenue: 40, expense: 32 },
  '78062': { revenue: 40, expense: 32 },
  '78063': { revenue: 30, expense: 24 },
  '78065': { revenue: 20, expense: 16 },
  '78066': { revenue: 20, expense: 16 },
  '78070': { revenue: 20, expense: 16 },
  '78074': { revenue: 30, expense: 24 },
  '78114': { revenue: 20, expense: 16 },
  '78115': { revenue: 20, expense: 16 },
  '78121': { revenue: 20, expense: 16 },
  '78123': { revenue: 20, expense: 16 },
  '78130': { revenue: 25, expense: 20 },
  '78131': { revenue: 25, expense: 20 },
  '78132': { revenue: 30, expense: 24 },
  '78133': { revenue: 80, expense: 64 },
  '78135': { revenue: 25, expense: 20 },
  '78155': { revenue: 20, expense: 16 },
  '78156': { revenue: 20, expense: 16 },
  '78163': { revenue: 15, expense: 12 },
  // '78253': { revenue: 15, expense: 12 },
  '78254': { revenue: 15, expense: 12 },
  '78261': { revenue: 15, expense: 12 },
  '78602': { revenue: 25, expense: 20 },
  '78605': { revenue: 40, expense: 32 },
  '78606': { revenue: 40, expense: 32 },
  '78608': { revenue: 40, expense: 32 },
  '78611': { revenue: 60, expense: 48 },
  '78612': { revenue: 20, expense: 16 },
  '78615': { revenue: 15, expense: 12 },
  '78616': { revenue: 30, expense: 24 },
  '78619': { revenue: 15, expense: 12 },
  '78620': { revenue: 20, expense: 16 },
  '78621': { revenue: 20, expense: 16 },
  '78622': { revenue: 30, expense: 24 },
  '78623': { revenue: 50, expense: 40 },
  '78624': { revenue: 85, expense: 68 },
  '78628': { revenue: 15, expense: 12 },
  '78629': { revenue: 40, expense: 32 },
  '78632': { revenue: 90, expense: 72 },
  '78633': { revenue: 18, expense: 14 },
  '78635': { revenue: 90, expense: 72 },
  '78636': { revenue: 85, expense: 68 },
  '78638': { revenue: 30, expense: 24 },
  '78639': { revenue: 120, expense: 96 },
  '78640': { revenue: 15, expense: 12 },
  '78641': { revenue: 15, expense: 12 },
  '78642': { revenue: 30, expense: 24 },
  '78643': { revenue: 75, expense: 60 },
  '78644': { revenue: 25, expense: 20 },
  '78645': { revenue: 25, expense: 20 },
  '78648': { revenue: 35, expense: 28 },
  '78650': { revenue: 20, expense: 16 },
  '78654': { revenue: 65, expense: 52 },
  '78655': { revenue: 30, expense: 24 },
  '78656': { revenue: 30, expense: 24 },
  '78657': { revenue: 75, expense: 60 },
  '78659': { revenue: 30, expense: 24 },
  '78661': { revenue: 30, expense: 24 },
  '78662': { revenue: 40, expense: 32 },
  '78663': { revenue: 60, expense: 48 },
  '78666': { revenue: 25, expense: 20 },
  '78667': { revenue: 25, expense: 20 },
  '78669': { revenue: 25, expense: 20 },
  '78670': { revenue: 30, expense: 24 },
  '78672': { revenue: 190, expense: 152 },
  '78673': { revenue: 25, expense: 20 },
  '78674': { revenue: 25, expense: 20 },
  '78676': { revenue: 30, expense: 24 },
  '78701': { revenue: 15, expense: 12 },
  '78738': { revenue: 20, expense: 16 },
  '78850': { revenue: 100, expense: 80 },
  '78861': { revenue: 40, expense: 32 },
  '78886': { revenue: 40, expense: 32 },
  '78947': { revenue: 40, expense: 32 },
  '78953': { revenue: 60, expense: 48 },
  '78957': { revenue: 30, expense: 24 },
  '78609': { revenue: 95, expense: 76 },
};

/**
 * The travel fee for a ZIP on an order created at `created`: the same table
 * choice TwtOrder makes for the order-level "Travel Compensation" line (the
 * newest version table that lists the ZIP, else the base table). Read by Ask
 * Twist Support's price list, so a quote and an invoice never disagree.
 */
export function twt_travel_fee(zip: string, created: TDateISO | DateTime | string): { revenue: number; expense: number } | undefined {
  const at = created as TDateISO | DateTime;

  if (TWT_TRAVEL_FEES_V24[zip] && twt_version(at, TwtVersion.V24)) {
    return TWT_TRAVEL_FEES_V24[zip];
  }

  if (TWT_TRAVEL_FEES_V22[zip] && twt_version(at, TwtVersion.V22)) {
    return TWT_TRAVEL_FEES_V22[zip];
  }

  if (TWT_TRAVEL_FEES_V21[zip] && twt_version(at, TwtVersion.V21)) {
    return TWT_TRAVEL_FEES_V21[zip];
  }

  return TWT_TRAVEL_FEES[zip];
}
